import { useEffect, useState } from "react";
import { callApi } from "../../services/ApiServices";
import { ErrorMessage, validateRequired } from "../../utlis/function";
import { useGlobalStates } from "../../utlis/ClubsContext";
import { UserRole } from "../../utlis/constant";

// card: { label, numberKey, titleKey?, descriptionKey } — each key is a flat club field
const EditableHighlightCard = ({ card }) => {
  const { selectedClub, handleSelectedClubDetail, userRole } =
    useGlobalStates();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const getValues = () => ({
    number: selectedClub?.[card.numberKey] ?? "",
    title: card.titleKey ? selectedClub?.[card.titleKey] ?? "" : "",
    description: selectedClub?.[card.descriptionKey] ?? "",
  });
  const [editValues, setEditValues] = useState(getValues);

  useEffect(() => {
    setEditValues(getValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClub, card]);

  const handleChange = (field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSave = async () => {
    const newErrors = {};
    if (!validateRequired(String(editValues.number))) {
      newErrors.number = "Number is required";
    }
    if (card.titleKey && !validateRequired(String(editValues.title))) {
      newErrors.title = "Title is required";
    }
    if (!validateRequired(String(editValues.description))) {
      newErrors.description = "Description is required";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const fields = {
      [card.numberKey]: String(editValues.number).trim(),
      [card.descriptionKey]: String(editValues.description).trim(),
    };
    if (card.titleKey) fields[card.titleKey] = String(editValues.title).trim();

    setIsSaving(true);
    try {
      const response = await callApi({
        method: "put",
        url: `club/update/${selectedClub?.id}`,
        data: { fields },
      });
      if (response.status === 200) {
        await handleSelectedClubDetail(selectedClub);
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Update failed", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValues(getValues());
    setErrors({});
    setIsEditing(false);
  };

  const values = getValues();
  const inputClass = (field) =>
    `w-full px-3 py-2 text-black border rounded-lg focus:outline-none focus:border-primary transition-colors ${
      errors[field] ? "border-red-500" : "border-gray-300"
    }`;

  return (
    <div className="bg-white rounded-xl p-6 main-shadow">
      <div className="flex items-start justify-between mb-4">
        <p className="text-base font-normal text-textcolor tracking-[0] leading-[normal]">
          {card.label}
        </p>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            disabled={userRole === UserRole.Viewer}
            className={`flex items-center  ${userRole === UserRole.Viewer ? "cursor-not-allowed opacity-50" : "cursor-pointer"} gap-1 text-base font-medium text-black hover:text-primary transition-colors`}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.3879 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Edit
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-3">
          <div>
            <input
              type="text"
              value={editValues.number}
              onChange={(e) => handleChange("number", e.target.value)}
              placeholder="Number (e.g. 74 min, 84%)"
              className={`${inputClass("number")} text-2xl font-semibold`}
              autoFocus
            />
            <ErrorMessage message={errors.number} />
          </div>
          {card.titleKey && (
            <div>
              <input
                type="text"
                value={editValues.title}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder="Title"
                className={`${inputClass("title")} text-base font-semibold`}
              />
              <ErrorMessage message={errors.title} />
            </div>
          )}
          <div>
            <textarea
              rows="3"
              value={editValues.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Description"
              className={`${inputClass("description")} text-sm resize-none`}
            />
            <ErrorMessage message={errors.description} />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 px-4 py-2 bg-primary hover:bg-hover text-white font-medium text-sm rounded-lg transition-colors"
            >
              {isSaving ? (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </div>
              ) : (
                "Save"
              )}
            </button>
            <button
              onClick={handleCancel}
              className="flex-1 px-4 py-2 bg-white border border-gray-200 text-textcolor font-medium text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          <h2 className="text-[40px] font-semibold text-black tracking-[0] leading-[48px] break-words">
            {values.number || "0"}
          </h2>
          {card.titleKey && values.title && (
            <h3 className="text-lg font-semibold text-black mt-2">
              {values.title}
            </h3>
          )}
          {values.description && (
            <p className="text-base font-normal text-textcolor leading-[24px] mt-2">
              {values.description}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default EditableHighlightCard;
